import { bytesToBase64Url } from "@zeroclipboard/shared";
import { exportRawPublic, generateX25519, sha256 } from "./primitives";
import { STORE_IDENTITY, idbGet, idbPut, idbDelete } from "./idb";

/** A device's long-term X25519 identity. The private key is non-extractable. */
export interface DeviceIdentity {
  deviceId: string;
  privateKey: CryptoKey;
  publicKey: CryptoKey;
  publicKeyRaw: Uint8Array;
  publicKeyB64: string;
}

interface StoredIdentity {
  privateKey: CryptoKey;
  publicKey: CryptoKey;
  publicKeyRaw: ArrayBuffer;
}

const SELF_KEY = "self";

/** The public routing id is derived from the public key (first 16 bytes of its
 *  SHA-256), so it is stable and self-authenticating against the key. */
export async function deriveDeviceId(
  publicKeyRaw: Uint8Array
): Promise<string> {
  const h = await sha256(publicKeyRaw);
  return bytesToBase64Url(h.subarray(0, 16));
}

export async function loadOrCreateIdentity(): Promise<DeviceIdentity> {
  const existing = await idbGet<StoredIdentity>(STORE_IDENTITY, SELF_KEY);
  if (existing) {
    const publicKeyRaw = new Uint8Array(existing.publicKeyRaw);
    return {
      deviceId: await deriveDeviceId(publicKeyRaw),
      privateKey: existing.privateKey,
      publicKey: existing.publicKey,
      publicKeyRaw,
      publicKeyB64: bytesToBase64Url(publicKeyRaw),
    };
  }

  const pair = await generateX25519();
  const publicKeyRaw = await exportRawPublic(pair.publicKey);
  const stored: StoredIdentity = {
    privateKey: pair.privateKey,
    publicKey: pair.publicKey,
    // Copy into a standalone ArrayBuffer for structured-clone storage.
    publicKeyRaw: publicKeyRaw.slice().buffer,
  };
  await idbPut(STORE_IDENTITY, SELF_KEY, stored);

  return {
    deviceId: await deriveDeviceId(publicKeyRaw),
    privateKey: pair.privateKey,
    publicKey: pair.publicKey,
    publicKeyRaw,
    publicKeyB64: bytesToBase64Url(publicKeyRaw),
  };
}

/**
 * Permanently wipe this device's identity from the OS keystore (IndexedDB).
 * Per spec §55, `revoke_device` deletes local keys; after this the next
 * `loadOrCreateIdentity()` mints a brand-new identity with a fresh deviceId.
 */
export async function deleteIdentity(): Promise<void> {
  await idbDelete(STORE_IDENTITY, SELF_KEY);
}
